import React, { useState, useEffect, lazy, Suspense, useCallback } from "react";
import { Routes, Route, useNavigate, Navigate, useLocation } from "react-router-dom";
import Swal from "sweetalert2";
import { useIdle } from "react-use";

// Layouts
import NonAuthLayout from "../Layouts/NonAuthLayout";
import VerticalLayout from "../Layouts";

// Common
import UserSessionVerify from "../common/utilities/UserSessionVerify/UserSessionVerify";

// Routes
import { authProtectedRoutes, publicRoutes } from "./allRoutes";
import { DASHBOARD_PATHS, SCANNING_PATHS, ACCESSIONING_PATHS } from "./paths";
import { AuthProtected } from "./AuthProtected";
import RouteGuard from "./RouteGuard";

// Services
import { GetUserData, userLogout, GetSystemSettingValue } from "../common/service/CommonService";
import TeamsSidebar from "../Layouts/VistaSidebar";
import { Button, Col, Modal, ModalBody, Row } from "reactstrap";
import styles from "../assets/scss/common/common.module.scss";
import { usePermission } from "context/PermissionContext";
import AccessRestricted from "Components/Common/AccessRestricted";
import { PERMISSION_MODULES } from "common/constants/PermissionConstants";
import MultiTabRestrict from "modules/Authentication/MultitabRestrict";

const user = GetUserData();

export let idealTimeout = 300000;

export const updateIdealTimeout = () => {
  const cached = GetSystemSettingValue(2) || GetSystemSettingValue("Session Timeout (Minutes)");
  if (cached) {
    const minutes = parseInt(cached, 10);
    if (!isNaN(minutes)) {
      idealTimeout = minutes * 60 * 1000;
      return;
    }
  }
  const user = GetUserData();
  idealTimeout = typeof user?.idealtimeout === "number"
    ? user.idealtimeout * 60 * 1000
    : 300000; // default 5 min
};

updateIdealTimeout();

const Loader = () => (
  <div className="flex justify-center items-center h-screen bg-white">
    <div className="animate-spin h-10 w-10 border-4 border-blue-600 border-t-transparent rounded-full" />
  </div>
);

const Index = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { hasPermission, isModuleVisible } = usePermission();

  useEffect(() => {
    const handleViewportRedirect = () => {
      const currentPath = window.location.pathname;
      const width = window.innerWidth;
      const isLoggedIn = Boolean(sessionStorage.getItem("currentUser"));

      if (!isLoggedIn) return;

      // On tablet or smaller screens, redirect from dashboard/index to scanning
      // if (
      //   width <= 1024 &&
      //   [DASHBOARD_PATHS.DEFAULT, "/index"].includes(currentPath)
      // ) {
      //   if (hasPermission(PERMISSION_MODULES.SESSION) && isModuleVisible(PERMISSION_MODULES.SESSION)) {
      //     navigate(SCANNING_PATHS.VIEW, { replace: true });
      //   }
      //   else if (hasPermission(PERMISSION_MODULES.DASHBOARD) && isModuleVisible(PERMISSION_MODULES.DASHBOARD)) {
      //     navigate(DASHBOARD_PATHS.DEFAULT, { replace: true });
      //   }
      //   else {
      //     <AccessRestricted />
      //   }
      // }
      if (
        width <= 1024 && (
          [DASHBOARD_PATHS.DEFAULT, "/index"].includes(currentPath) || [ACCESSIONING_PATHS.DASHBOARD, "/index"].includes(currentPath))
      ) {
        if (hasPermission(PERMISSION_MODULES.SESSION) && isModuleVisible(PERMISSION_MODULES.SESSION)) {
          navigate(SCANNING_PATHS.VIEW, { replace: true });
        }
        else if (hasPermission(PERMISSION_MODULES.DASHBOARD) && isModuleVisible(PERMISSION_MODULES.DASHBOARD)) {
          navigate(DASHBOARD_PATHS.DEFAULT, { replace: true });
        }
        else if (hasPermission(PERMISSION_MODULES.SESSION) && isModuleVisible(PERMISSION_MODULES.SESSION)) {
          navigate(ACCESSIONING_PATHS.DASHBOARD, { replace: true });
        }
        else {
          <AccessRestricted />
        }
      }
    };

    handleViewportRedirect();
    window.addEventListener("resize", handleViewportRedirect);
    return () => window.removeEventListener("resize", handleViewportRedirect);
  }, [navigate]);

  // Multi-tab restriction
  const [accessBlocked, setAccessBlocked] = useState(false);

  // Session verification modal
  const [isVerifyReasonModal, setIsVerifyReasonModal] = useState(false);
  const toggleVerifyReasonModal = () =>
    setIsVerifyReasonModal((prev) => !prev);

  // Idle detection
  const [timeoutValue, setTimeoutValue] = useState(idealTimeout);
  const isIdle = useIdle(timeoutValue);
  const [alertShown, setAlertShown] = useState(false);

  useEffect(() => {
    updateIdealTimeout();
    setTimeoutValue(idealTimeout);

    const handleStorageChange = () => {
      updateIdealTimeout();
      setTimeoutValue(idealTimeout);
    };

    window.addEventListener("storage", handleStorageChange);
    return () => {
      window.removeEventListener("storage", handleStorageChange);
    };
  }, []);

  // ============================================================
  // 🧩 Multi-tab Restriction
  // ============================================================
  useEffect(() => {
    const channel = new BroadcastChannel("BlockMultipleTabs");

    channel.postMessage("Tab Opened");
    channel.onmessage = (e) => {
      if (e.data === "Tab Opened") {
        channel.postMessage("Block");
      } else if (e.data === "Block") {
        setAccessBlocked(true);
        channel.close();
      }
    };

    return () => channel.close();
  }, []);

  // ============================================================
  // 🧩 Session Timeout Logic
  // ============================================================
  const userSessionVerified = () => {
    setAlertShown(false);
    sessionStorage.setItem("IsIdealTimeOut", "0");
  };

  const handleLogout = () => {
    setAlertShown(false);
    sessionStorage.setItem("IsIdealTimeOut", "0");
    userLogout(navigate);
  };

  const handleCountinue = () => {
    toggleVerifyReasonModal();
  };

  const checkSessionTimeout = () => {
    const localStorageLength = sessionStorage.length > 0;
    const currentUser = sessionStorage.getItem("currentUser");
    const isIdealTimeOut = sessionStorage.getItem("IsIdealTimeOut") === "1";

    if (localStorageLength && currentUser && (isIdle || isIdealTimeOut) && !alertShown) {
      sessionStorage.setItem("IsIdealTimeOut", "1");
      setAlertShown(true);


    }
  };

  useEffect(() => {
    const isIdealTimeOut = sessionStorage.getItem("IsIdealTimeOut") === "1";
    if (isIdle || isIdealTimeOut) checkSessionTimeout();
  }, [isIdle]);

  // ============================================================
  // 🧭 Routing
  // ============================================================
  return (
    <>
      <Suspense fallback={<Loader />}>
        <Routes>
          {/* 🏠 Root Redirect */}
          <Route
            path="/"
            element={
              sessionStorage.getItem("currentUser") ? (
                <Navigate to={window.innerWidth <= 1024 && isModuleVisible(PERMISSION_MODULES.SESSION) ? SCANNING_PATHS.VIEW : ACCESSIONING_PATHS.DASHBOARD} replace />
              ) : (
                <Navigate to="/login" replace />
              )
            }
          />

          {/* 🔓 Public Routes */}
          {publicRoutes.map(({ path, component }, idx) => (
            <Route
              key={idx}
              path={path}
              element={<NonAuthLayout>{component}</NonAuthLayout>}
            />
          ))}

          {/* 🔒 Auth Protected Routes */}
          {authProtectedRoutes.map((item, idx) => {
            const { path, component, showHeader = true, module, action } = item;

            // If a module is specified, wrap the component with RouteGuard
            const guardedComponent = module ? (
              <RouteGuard module={module} action={action}>{component}</RouteGuard>
            ) : component;

            return (
              <Route
                key={idx}
                path={path}
                element={
                  accessBlocked ? (
                    <MultiTabRestrict />
                  ) : (
                    <AuthProtected>
                      <>
                        <VerticalLayout showHeader={showHeader}>{guardedComponent}</VerticalLayout>
                        {isVerifyReasonModal && (
                          <UserSessionVerify
                            isOpen={isVerifyReasonModal}
                            toggle={toggleVerifyReasonModal}
                            click={userSessionVerified}
                          />
                        )}
                      </>
                    </AuthProtected>
                  )
                }
              />
            );
          })}

          {/* ✅ Catch-all redirect */}
          <Route path="*" element={<Navigate to="/login" />} />
        </Routes>
      </Suspense>
      {/* Session Expiry Modal */}
      {alertShown && (
        <Modal isOpen={alertShown} centered backdrop="static" backdropClassName="session-timeout-modal" className='session-timeout-modal'>
          <ModalBody>
            <Row>
              <Col className="text-center">
                <div className={`${styles.timeoutIcon} mb-3`}>
                  <svg width="60" height="60" fill="none" viewBox="0 0 24 24" className={styles.timeoutSvgAnimated}>
                    {/* Watch Body */}
                    <circle cx="12" cy="12" r="10" stroke="#1E6B91" strokeWidth="1.5" fill="#e6f2fa" />

                    {/* Static Hand (Hour) */}
                    <line x1="12" y1="12" x2="15" y2="8" stroke="#547996ff" strokeWidth="1.5" strokeLinecap="round" />

                    {/* Animated Hand (Minute) - Rotates 360 degrees */}
                    <path d="M12 12V6" stroke="#1E6B91" strokeWidth="2" strokeLinecap="round" className={styles.timeoutHand} />

                    {/* Center Pin */}
                    <circle cx="12" cy="12" r="1.5" fill="#1E6B91" />
                  </svg>
                </div>
                <h4 className="mb-2 mt-1">Session Timeout</h4>
                <p className="mb-4">
                  We haven't detected any recent activity. Your session will expire soon.<br />
                  Would you like to continue?
                </p>
                <div className="d-flex justify-content-center gap-3 mt-2">
                  <Button color='primary'
                    // className="continue-btn fs-6 d-flex align-items-center "
                    onClick={() => { handleCountinue(); }}>
                    Continue Session
                  </Button>
                  <Button color="light"
                    className="border"
                    onClick={handleLogout}>
                    Logout
                  </Button>
                </div>
              </Col>
            </Row>
          </ModalBody>
        </Modal>)}

    </>
  );
};

export default Index;
